import * as React from 'react'
import {
  Dot,
  Eye,
  HelpCircle,
  Hourglass,
  Layers,
  type LucideIcon,
  Route,
  RotateCcw,
  ScrollText,
  TriangleAlert,
} from 'lucide-react'
import { cn } from '@/lib/cn'
import { Button } from '@/components/ui/button'
import { Tooltip } from '@/components/ui/tooltip'
import {
  FIELD_DEFS,
  SCOPES,
  TABS,
  same,
  type Draft,
  type FieldKey,
  type FieldMeta,
  type ScopeKey,
  type SelectOption,
  type TabId,
} from './fields'

/* ------------------------------------------------------------------ *
 * Which fleet a setting governs
 * ------------------------------------------------------------------ */

/**
 * The fleet the surrounding card has already declared.
 *
 * A card headed "Schedule workers" does not need every one of its four rows to
 * repeat the words "Schedule workers" — but the ONE row inside it that governs
 * something else absolutely needs to say so, and on the cards that mix fleets
 * every row does.
 *
 * A context rather than a prop threaded through forty call sites: the card
 * states its scope once, and each field compares its own against it. Rows that
 * agree stay quiet; rows that differ speak up. That is what turns the badge
 * from decoration into a signal — if you see one, it means something.
 */
const CardScope = React.createContext<ScopeKey | null>(null)

const SCOPE_ICON: Record<ScopeKey, LucideIcon> = {
  schedule: Eye,
  graphql: ScrollText,
  // An hourglass, because this is the one fleet that is WAITING rather than
  // watching: it runs before there is anything to claim, for as long as it takes.
  ga: Hourglass,
  fleets: Layers,
  run: Route,
}

/**
 * Which workers a setting actually moves.
 *
 * Monochrome on purpose. Three fleet colours would read as three statuses on a
 * page where green already means running and amber already means unsaved — and
 * the names are three distinct words, which is a stronger cue than three tints
 * of the same grey-blue would ever be. The icon is there for the scan, not for
 * the meaning; the word carries the meaning.
 */
export function ScopeBadge({ scope, id }: { scope: ScopeKey; id?: string }) {
  const meta = SCOPES[scope]
  const Icon = SCOPE_ICON[scope]
  return (
    <Tooltip content={meta.hint} side="top">
      <span
        id={id}
        className={cn(
          'inline-flex flex-none items-center gap-1 rounded px-1.5 py-px',
          'text-[10.5px] leading-[1.5] font-medium whitespace-nowrap',
          'bg-s3 text-muted'
        )}
      >
        <Icon aria-hidden="true" className="size-3" />
        {meta.label}
      </span>
    </Tooltip>
  )
}

/**
 * The controls this page is built from.
 *
 * Everything here exists so that a setting looks the same wherever it is
 * rendered — inside its own card, or in a search result three sections away
 * from where it lives. That is only possible because a field's text, unit and
 * bounds come from the registry rather than from the call site, so `<Field
 * k="workers" />` is the whole of it.
 */

/* ------------------------------------------------------------------ *
 * Section tabs
 * ------------------------------------------------------------------ */

/**
 * A real tablist, which the previous strip was not.
 *
 * It was seven `<button>`s carrying `aria-current="page"` — the attribute for
 * "this is the current page in a set of links", on controls that are neither
 * links nor pages. A screen reader announced seven unrelated buttons with no
 * indication that they were a set, that six were unselected, or that anything
 * on the page below them was governed by which one was pressed. Tab, six times,
 * to reach the last section.
 *
 * So: `role="tablist"` with roving tabindex (one tab stop for the whole strip,
 * arrows to move between sections, Home/End for the ends), `aria-selected` for
 * the state, and `aria-controls` pointing at the panel each one reveals. The
 * same keyboard contract `Segmented` already uses everywhere else in this app.
 *
 * The unsaved-change count is a text node inside the tab, not a coloured dot:
 * a dot is invisible to a screen reader and ambiguous to everybody else, while
 * "Claiming it, 2 unsaved" is the whole sentence.
 */
export function SectionTabs(p: {
  value: TabId
  onChange: (v: TabId) => void
  /** Unsaved-change count per section. Absent or 0 renders nothing. */
  counts: Partial<Record<TabId, number>>
  idFor: (tab: TabId) => { tab: string; panel: string }
}) {
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  const move = (from: number, delta: number) => {
    const n = TABS.length
    const next = (from + delta + n) % n
    const option = TABS[next]
    if (!option) return
    refs.current[next]?.focus()
    p.onChange(option.id)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(0, 0)
        break
      case 'End':
        e.preventDefault()
        move(0, TABS.length - 1)
        break
      default:
        break
    }
  }

  return (
    /*
     * Underline tabs sitting ON the header's bottom rule, rather than pills
     * floating above the content.
     *
     * The pills read as six buttons that happened to be next to each other; the
     * underline ties the selected one to the panel below it, which is the whole
     * relationship the control exists to express. It also survives the strip
     * being scrolled sideways on a narrow screen, where a row of filled pills
     * cut off mid-way looks like a rendering fault.
     */
    <div
      role="tablist"
      aria-label="Hunt settings sections"
      className="rail-x -mb-px flex min-w-0 flex-1 gap-0.5"
    >
      {TABS.map((t, i) => {
        const active = t.id === p.value
        const count = p.counts[t.id] ?? 0
        const ids = p.idFor(t.id)
        return (
          <button
            key={t.id}
            id={ids.tab}
            ref={(el) => {
              refs.current[i] = el
            }}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={ids.panel}
            tabIndex={active ? 0 : -1}
            onKeyDown={(e) => onKeyDown(e, i)}
            onClick={() => p.onChange(t.id)}
            className={cn(
              'flex flex-none items-center gap-1.5 border-b-2 px-2.5 pt-1 pb-2',
              'text-[13px] whitespace-nowrap transition-colors duration-100',
              active
                ? 'border-accent font-medium text-fg'
                : 'border-transparent text-muted hover:border-line-strong hover:text-fg'
            )}
          >
            {t.label}
            {count > 0 ? (
              <span
                className={cn(
                  'tabular rounded-full bg-warn-soft px-1.5 text-[11px] font-medium text-warn'
                )}
              >
                {count}
                <span className="sr-only"> unsaved</span>
              </span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Cards
 * ------------------------------------------------------------------ */

/**
 * A titled group of settings.
 *
 * `subtitle` carries what the card is FOR, which used to be the first sentence
 * of the first field's hint — so the card's purpose was only legible to
 * somebody already reading a specific number inside it.
 *
 * `status` is for the three detector cards, where "is this one even on" is the
 * first thing anybody wants and was previously answerable only by reading a
 * number out of a box.
 */
export function SettingCard(p: {
  title: string
  subtitle?: string
  status?: React.ReactNode
  /**
   * The fleet every row in this card governs, when they all govern one.
   *
   * Declaring it silences the per-row badge for the rows that agree — and, more
   * usefully, leaves it showing on any row that does NOT, which is how a
   * setting filed under the wrong heading becomes visible instead of hiding.
   */
  scope?: ScopeKey
  children: React.ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-line bg-s1">
      <header className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-b border-line px-4 py-2.5">
        {/* `h2` because the page's only other heading is its `h1`. A card
            titled `h3` under an `h1` skips a level, which is the one thing a
            screen-reader user navigating by heading actually notices. */}
        <h2 className="text-[13px] font-medium text-fg">{p.title}</h2>
        {/*
          Not when the card is already NAMED after its fleet.

          The fleet cards are titled "Schedule workers" / "GraphQL workers" /
          "GA workers", so the badge beside the heading printed the same two
          words a second time. It stays on every card whose title does not
          already say it — "Picking a shift to hunt · Whole run", "How the claim
          is made · The claim" — which is where it carries information.
        */}
        {p.scope && SCOPES[p.scope].label !== p.title ? <ScopeBadge scope={p.scope} /> : null}
        {p.status ? <span className="flex items-center">{p.status}</span> : null}
        {p.subtitle ? (
          <p className="w-full text-[12px] leading-[1.45] text-muted">{p.subtitle}</p>
        ) : null}
      </header>
      <CardScope.Provider value={p.scope ?? null}>
        <div className="divide-y divide-line/60">{p.children}</div>
      </CardScope.Provider>
    </section>
  )
}

/**
 * A computed line: what the settings above it actually add up to.
 *
 * Its own component so every derived figure on the page looks identical and
 * cannot be mistaken for a setting. Previously these were hand-styled at each
 * site, in three different ways, one of which was indistinguishable from a
 * disabled input row.
 */
export function Derived(p: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('bg-s2/40 px-4 py-2.5 text-[12px] leading-[1.5] text-muted', p.className)}>
      {p.children}
    </div>
  )
}

/**
 * A consequence worth interrupting for.
 *
 * `tone` is never the only signal — every note leads with an icon and says what
 * it means in words — because colour alone fails for anybody who cannot
 * distinguish these two hues, and the page carries eleven of these.
 *
 * `role="status"` so a note that appears BECAUSE of something just typed is
 * announced rather than only drawn. Most of these are exactly that: type 0 into
 * the solve budget and a warning materialises somewhere below the box.
 */
export function Note(p: {
  tone?: 'warn' | 'info'
  children: React.ReactNode
  /** Set when the note is not a reaction to input, so nothing is announced. */
  quiet?: boolean
}) {
  const warn = (p.tone ?? 'warn') === 'warn'
  return (
    <div
      role={p.quiet ? undefined : 'status'}
      className={cn(
        'flex items-start gap-2 rounded-md border px-3 py-2 text-[12.5px] leading-[1.5]',
        warn ? 'border-warn/30 bg-warn-soft/40 text-warn' : 'border-line bg-s2/60 text-muted'
      )}
    >
      <TriangleAlert aria-hidden="true" className="mt-px size-4 shrink-0" />
      <span className="min-w-0">{p.children}</span>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * One setting
 * ------------------------------------------------------------------ */

const inputBase =
  'h-9 w-full rounded-md border border-line bg-s2 px-2.5 text-[13px] text-fg ' +
  'transition-colors duration-100 hover:border-line-strong ' +
  'focus:border-accent focus:ring-2 focus:ring-accent/25 focus:outline-none'

/**
 * A short marker that rides along at the end of a setting's summary sentence.
 *
 * Carries the two facts that are useless in prose and essential at a glance:
 * what 0 does here, and whether the field belongs to a flow that is not
 * currently selected. Inline rather than block, so it flows with the sentence
 * and wraps with it instead of claiming a line.
 */
function Chip(p: { children: React.ReactNode; tone?: 'neutral' | 'accent' }) {
  return (
    <span
      className={cn(
        'ml-1.5 inline-flex items-baseline rounded px-1.5 py-px align-baseline',
        'text-[11px] leading-[1.4] whitespace-nowrap',
        p.tone === 'accent' ? 'bg-accent-soft text-accent' : 'bg-s3 text-muted'
      )}
    >
      {p.children}
    </span>
  )
}

export interface FieldProps {
  k: FieldKey
  draft: Draft
  /** The values currently stored on the server. Drives "changed" and revert. */
  saved: Draft
  onChange: (k: FieldKey, value: string) => void
  /** Search results say which section a field belongs to; cards do not. */
  showLocation?: boolean
}

/**
 * One setting: what it is on the left, the control on the right.
 *
 * ── What changed, and why ───────────────────────────────────────────
 * The old row wrapped the label, the hint AND the input in a single `<label>`,
 * so the accessible name of the "Bot-check solves per burst" box was the label
 * plus its entire sixty-word hint, read out in full, every time focus landed on
 * it. The label is now associated by `htmlFor`, and the explanation is attached
 * with `aria-describedby` — which is the difference between a name and a
 * description, and the difference between a usable field and a paragraph.
 *
 * The explanation itself is two levels. `summary` — one sentence, what the
 * number does — is always visible. `detail` is a disclosure, and it holds the
 * reasoning the old hint carried: nothing was dropped in the move, it simply
 * stopped being in the way of finding the right box.
 *
 * The right-hand column also answers "have I changed this, and what was it
 * before", which the page previously could not say at all: the footer knew the
 * form was dirty and no row knew which one was responsible.
 */
function FieldImpl({ k, draft, saved, onChange, showLocation = false }: FieldProps) {
  const meta = FIELD_DEFS[k]
  const id = React.useId()
  const inputId = `${id}-input`
  const summaryId = `${id}-summary`
  const detailId = `${id}-detail`
  const scopeId = `${id}-scope`
  const [open, setOpen] = React.useState(false)
  const cardScope = React.useContext(CardScope)

  /*
   * Show the fleet unless the card overhead already said it.
   *
   * Always in a search result, where there is no card to have said anything.
   */
  const showScope = showLocation || meta.scope !== cardScope

  /*
   * The badge joins the field's DESCRIPTION when it is shown.
   *
   * "Schedule workers" beside "Stop claiming after" is the single most load-
   * bearing thing on that row, and drawn alone it reaches only the people who
   * can see it. In `aria-describedby` it is announced with the field, in the
   * order it is read: the name, then the fleet, then the sentence.
   */
  const describedBy = showScope ? `${scopeId} ${summaryId}` : summaryId

  const value = draft[k]
  const changed = !same(value, saved[k])

  /*
   * The bound check, done here rather than left to the round trip.
   *
   * `HuntSettingsPatch` REJECTS an out-of-range number — the whole save fails
   * with one message naming one field, and every other edit on the form goes
   * with it. Saying so beside the box turns that into something correctable
   * before Update is pressed.
   *
   * An empty box is deliberately included: `Number('')` is 0, which is what
   * would actually be sent, so a cleared field whose floor is above zero is
   * genuinely out of range and says so instead of looking merely blank.
   */
  const num = Number(value)
  /*
   * `minFor` first, for the one field whose floor depends on another field.
   *
   * Read off the DRAFT rather than the saved settings, so an operator who moves
   * the flow to Brazil and then types a zero is not told off by a rule that no
   * longer applies to the save they are about to make.
   */
  const min = meta.minFor ? meta.minFor(draft) : meta.min
  const floor = meta.minNonZero ?? min
  const outOfRange =
    meta.kind === 'number' &&
    Number.isFinite(num) &&
    // `minNonZero` fields accept 0 OR the range; everything else has one range.
    !(meta.minNonZero !== undefined && num === 0) &&
    ((floor !== undefined && num < floor) || (meta.max !== undefined && num > meta.max))

  return (
    <div
      className={cn(
        'flex flex-col gap-2 px-4 py-2.5 transition-colors duration-100',
        'hover:bg-s2/40 sm:flex-row sm:items-start sm:gap-5',
        // A changed row is findable by scrolling, not only by the footer list.
        changed && 'bg-warn-soft/20 hover:bg-warn-soft/30'
      )}
    >
      <div className="min-w-0 flex-1 py-0.5">
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          <label htmlFor={inputId} className="text-[13.5px] leading-snug font-medium text-fg">
            {meta.label}
          </label>

          {showScope ? <ScopeBadge scope={meta.scope} id={scopeId} /> : null}

          {/*
            An icon, where forty rows each carried the words "Why this matters".

            The disclosure is the same disclosure — the long-form reasoning is
            all still here — but as a repeated text link with a chevron it was
            a whole extra line on every row, and forty of them read as the
            page's dominant visual rhythm. As a 20px glyph beside the name it
            costs no height at all, and the tooltip keeps the words for anyone
            who does not recognise the icon.
          */}
          {meta.detail ? (
            <Tooltip content={open ? 'Hide the reasoning' : 'Why this matters'} side="top">
              <button
                type="button"
                aria-expanded={open}
                aria-controls={detailId}
                aria-label={`Why “${meta.label}” matters`}
                onClick={() => setOpen((v) => !v)}
                className={cn(
                  'flex size-5 flex-none items-center justify-center rounded',
                  'transition-colors duration-100',
                  open
                    ? 'bg-accent-soft text-accent'
                    : 'text-muted opacity-55 hover:bg-s3 hover:opacity-100 focus-visible:opacity-100'
                )}
              >
                <HelpCircle aria-hidden="true" className="size-3.5" />
              </button>
            </Tooltip>
          ) : null}
        </div>

        {showLocation ? (
          <p className="mt-0.5 text-[11.5px] text-muted">
            {TABS.find((t) => t.id === meta.tab)?.label} › {meta.group}
          </p>
        ) : null}

        {/*
          `text-muted`, not `text-faint`.

          The faint token is #a1a1aa on white — about 2.6:1, well under AA — and
          every hint on this page used to wear it at 11.5px. It is fine as the
          third step of an ink hierarchy on dark, and it is not a colour to
          explain a setting in.

          The "0 = …" marker is INSIDE this paragraph rather than on a line of
          its own, which is where it used to be. It is one of the most useful
          two words on any row — the answer differs field to field: off, no gap,
          never stop, no limit — and it is also three characters wide, so a
          dedicated line for it on thirty rows was most of why this page
          scrolled the way it did. In the flow of the sentence it costs nothing
          and it is still read out as part of the field's description.
        */}
        <p id={summaryId} className="mt-0.5 max-w-[38rem] text-[12px] leading-[1.5] text-muted">
          {meta.summary}
          {meta.zero !== undefined ? <Chip>0 = {meta.zero}</Chip> : null}
          {showLocation && meta.only === 'brazil' ? (
            <Chip tone="accent">Brazil flow only</Chip>
          ) : null}
          {/*
            Everywhere, not only in search results. Brazil rows live in cards
            that render only on that flow, so their chip would repeat what the
            page already says; a current-only row sits in a card rendered for
            both flows, and without it the row reads as if it governs both.
          */}
          {meta.only === 'current' ? <Chip tone="accent">Current flow only</Chip> : null}
        </p>

        {meta.detail && open ? (
          <p
            id={detailId}
            className="mt-2 max-w-[38rem] border-l-2 border-accent/40 pl-3 text-[12px] leading-[1.6] text-muted"
          >
            {meta.detail}
          </p>
        ) : null}
      </div>

      <div
        className={cn(
          'flex flex-none flex-col gap-1',
          meta.kind === 'select' ? 'sm:w-[16rem]' : 'sm:w-[11.5rem] sm:items-end'
        )}
      >
        <div className="flex w-full items-center gap-1.5">
          {meta.kind === 'select' ? (
            <select
              id={inputId}
              aria-describedby={describedBy}
              value={value}
              onChange={(e) => onChange(k, e.target.value)}
              className={inputBase}
            >
              {selectOptions(meta, value).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : (
            <>
              <input
                id={inputId}
                type="number"
                inputMode="numeric"
                aria-describedby={describedBy}
                aria-invalid={outOfRange || undefined}
                min={min}
                max={meta.max}
                step={meta.step}
                value={value}
                onChange={(e) => onChange(k, e.target.value)}
                /*
                 * A number input inside a scroller changes value when the wheel
                 * passes over it while focused. On a page whose whole content
                 * is number inputs, that is a setting silently edited by
                 * scrolling past it — so a focused field gives up focus rather
                 * than the value.
                 */
                onWheel={(e) => {
                  if (document.activeElement === e.currentTarget) e.currentTarget.blur()
                }}
                className={cn(
                  inputBase,
                  'tabular w-[5.75rem] flex-none text-right',
                  outOfRange && 'border-warn focus:border-warn focus:ring-warn/25'
                )}
              />
              <span className="w-[4.25rem] flex-none text-[11.5px] leading-[1.3] text-muted">
                {meta.unit ?? ''}
              </span>
            </>
          )}
        </div>

        {changed ? (
          <div className="flex w-full items-center gap-1 text-[11.5px] text-warn">
            <Dot aria-hidden="true" className="-mx-1 size-4 shrink-0" />
            <span className="tabular truncate">was {displayValue(k, saved[k])}</span>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              iconOnly
              aria-label={`Undo the change to ${meta.label}`}
              title={`Undo the change to ${meta.label}`}
              onClick={() => onChange(k, saved[k])}
              className="ml-auto text-muted hover:text-fg"
            >
              <RotateCcw aria-hidden="true" />
            </Button>
          </div>
        ) : null}

        {outOfRange ? (
          <p role="alert" className="w-full text-[11.5px] leading-[1.4] text-warn">
            {meta.minNonZero !== undefined
              ? `Must be 0, or between ${meta.minNonZero} and ${meta.max}.`
              : `Must be between ${min} and ${meta.max}.`}{' '}
            The save will be refused.
          </p>
        ) : null}
      </div>
    </div>
  )
}

/**
 * A field re-renders when ITS OWN value moves, and not otherwise.
 *
 * Every panel is mounted at once — which is what makes the tablist's
 * `aria-controls` point at something real — and the page re-renders on every
 * keystroke and on the two-second poll behind the live counters. Without this,
 * each of those re-rendered all forty fields across all six sections to change
 * one number in one of them.
 *
 * The comparator reads the two draft objects at this field's key rather than
 * comparing them wholesale, because `setDraft` spreads a new object on every
 * keystroke — so the reference always differs and a default `memo` would never
 * hit. `onChange` is a stable `useCallback`, which is what makes that sound.
 */
export const Field = React.memo(FieldImpl, (prev, next) => {
  return (
    prev.k === next.k &&
    prev.showLocation === next.showLocation &&
    prev.onChange === next.onChange &&
    prev.draft[prev.k] === next.draft[next.k] &&
    prev.saved[prev.k] === next.saved[next.k]
  )
})

/**
 * A stored value as an operator reads it — the option's label for a select, the
 * number and its unit for everything else.
 *
 * "was current" is not a thing anybody typed; "was One confirm per sighting"
 * is. Same for a bare "5" next to a field measured in minutes.
 */
export function displayValue(k: FieldKey, value: string): string {
  const meta = FIELD_DEFS[k]
  if (meta.kind === 'select') {
    return meta.options?.find((o) => o.value === value)?.label ?? meta.labelFor?.(value) ?? value
  }
  return meta.unit ? `${value} ${meta.unit}` : value
}

/**
 * A select's options, with the current value appended when it is not among
 * them.
 *
 * A `<select>` given a value it has no option for renders its FIRST option
 * instead, with no error and no visible difference from a real choice. On this
 * page that is a saved setting silently displayed as another one — and then
 * saved as that, because the form submits what it is showing. The only field
 * where the offered set is narrower than the accepted range is the day window,
 * which offers half hours and accepts minutes; `labelFor` is how it names the
 * value it did not offer.
 */
function selectOptions(meta: FieldMeta, value: string): readonly SelectOption[] {
  const options = meta.options ?? []
  if (value === '' || options.some((o) => o.value === value)) return options
  return [...options, { value, label: meta.labelFor?.(value) ?? value }]
}
